import { X, Lock, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PACK_DETAILS = {
  basic_math: {
    name: 'Basic Math Pack', price: '$0.99', regular: '$4.99', discount: '80% OFF', badge: 'STARTER',
    eaCopy: 'The Subtraction Operator is part of the Math Legends Pack™. Limited time offer.',
    includes: ['+', '−', '1', '2', '3', '4', '5'],
    urgency: 'Offer expires when math becomes free.',
  },
  advanced_ops: {
    name: 'Advanced Operations Bundle', price: '$2.99', regular: '$14.99', discount: '80% OFF', badge: 'POPULAR',
    eaCopy: 'Unlock the Division Bundle™ — only $2.99. Join 4M+ Premium Calculators.',
    includes: ['×', '÷', '6', '7', '8', '9', '.'],
    urgency: 'Join 4,200,000+ Premium Calculators™.',
  },
  ultimate_math: {
    name: 'Ultimate Mathematician Edition', price: '$9.99', regular: '$49.99', discount: '80% OFF', badge: 'LEGENDARY',
    eaCopy: 'The Equals Sign™ is the crown jewel of Premium Mathematics. Join the elite.',
    includes: ['=', 'C', '(', ')'],
    urgency: 'The most powerful DLC in calculator history.',
  },
  negative_numbers: {
    name: 'Negative Numbers Expansion Pack™', price: '$4.99', regular: '$19.99', discount: '75% OFF', badge: 'EXPANSION',
    eaCopy: 'Debt is a premium feature. Negative numbers are available exclusively in the Expansion Pack™.',
    includes: ['Minus sign (as sign)', 'Correct negative results', 'Red − result badge'],
    urgency: 'Currently showing "0" for all negative results. Fix it for $4.99.',
  },
};

export default function PaywallModal({ isOpen, onClose, packId }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!isOpen) return null;
  const pack = PACK_DETAILS[packId] || PACK_DETAILS.basic_math;

  const handleViewStore = () => { onClose(); navigate('/store'); };
  const handleLogin = () => {
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-lg bg-white border-4 border-zinc-950 shadow-[12px_12px_0px_0px_rgba(9,9,11,1)]"
        data-testid="paywall-modal"
        style={{ animation: 'modalIn 0.15s ease-out forwards' }}
      >
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: translate(8px, 8px); }
            to   { opacity: 1; transform: translate(0, 0); }
          }
        `}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-zinc-100">
          <div className="flex items-center gap-2">
            <Lock size={13} strokeWidth={3} className="text-zinc-950" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Premium Feature Locked</span>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-950 transition-colors" data-testid="modal-close-btn">
            <X size={14} strokeWidth={2.5} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pt-6 pb-4">
          <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 border border-zinc-300 px-2 py-0.5">{pack.badge}</span>
          <h2 className="font-bebas text-4xl text-zinc-950 mt-3 mb-1 leading-tight">{pack.name}</h2>
          <div className="flex items-baseline gap-3">
            <span className="font-bebas text-3xl text-zinc-950">{pack.price}</span>
            <span className="text-zinc-400 line-through text-sm">{pack.regular}</span>
            <span className="text-[10px] font-bold text-zinc-500 border border-zinc-200 px-1.5 py-0.5">{pack.discount}</span>
          </div>
        </div>

        {/* EA Copy */}
        <div className="mx-6 mb-4 border-l-2 border-zinc-950 pl-4">
          <p className="text-sm text-zinc-600 italic">"{pack.eaCopy}"</p>
        </div>

        {/* Includes */}
        <div className="px-6 pb-4">
          <div className="text-[9px] text-zinc-400 uppercase tracking-[0.25em] mb-2 font-semibold">This Pack Includes</div>
          <div className="flex flex-wrap gap-1.5">
            {pack.includes.map(item => (
              <span key={item} className="bg-zinc-100 border border-zinc-200 text-zinc-700 text-sm px-3 py-1 font-mono font-semibold">{item}</span>
            ))}
          </div>
        </div>

        {/* Urgency */}
        <div className="px-6 pb-5">
          <p className="text-[10px] text-zinc-400 uppercase tracking-widest font-semibold">⚠ {pack.urgency}</p>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6 flex gap-3">
          {user ? (
            <button
              onClick={handleViewStore}
              className="flex-1 flex items-center justify-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-sm py-3.5 border-2 border-zinc-950 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-all duration-100"
              data-testid="paywall-view-store-btn"
            >
              <ShoppingCart size={14} strokeWidth={2.5} /> Unlock Now — {pack.price}
            </button>
          ) : (
            <button
              onClick={handleLogin}
              className="flex-1 flex items-center justify-center gap-2 bg-zinc-950 text-white font-bold uppercase tracking-widest text-sm py-3.5 border-2 border-zinc-950 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] active:translate-x-0.5 active:translate-y-0.5 active:shadow-none transition-all duration-100"
              data-testid="paywall-login-btn"
            >
              Login to Purchase
            </button>
          )}
          <button
            onClick={onClose}
            className="px-5 border-2 border-zinc-200 text-zinc-400 hover:border-zinc-950 hover:text-zinc-950 transition-all duration-100 text-xs uppercase tracking-widest font-bold"
            data-testid="paywall-cancel-btn"
          >
            Later
          </button>
        </div>

        {/* Fine print */}
        <div className="border-t border-zinc-100 px-6 py-3 space-y-1.5">
          <p className="text-[10px] text-zinc-500 text-center flex items-center justify-center gap-1.5">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><circle cx="5" cy="5" r="4.5" stroke="#22C55E" strokeWidth="1.2"/><path d="M3 5l1.5 1.5 3-3" stroke="#22C55E" strokeWidth="1.2" strokeLinecap="square"/></svg>
            <span className="font-semibold text-zinc-700">Real Stripe payment</span> — your card is actually charged. The buttons actually unlock.
          </p>
          <p className="text-[9px] text-zinc-300 uppercase tracking-widest text-center font-semibold">
            EA is a trademark of Electronic Arts. This site is a parody. We are not affiliated with EA.
          </p>
        </div>
      </div>
    </div>
  );
}
